/**
 * i18n (app.md §9) — i18next + react-i18next.
 * Carrega SOMENTE o idioma ativo (os JSON têm ~55 KB cada). Mesmas chaves do legado.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { SupportedLang } from '../config/constants';
import { SUPPORTED_LANGS } from '../config/constants';

type Dict = Record<string, string>;

const loaders: Record<SupportedLang, () => Promise<{ default: Dict }>> = {
  pt: () => import('./locales/pt.json'),
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
  fr: () => import('./locales/fr.json'),
};

function normalizeLang(lang: string | undefined): SupportedLang {
  const short = (lang ?? 'en').slice(0, 2).toLowerCase() as SupportedLang;
  return SUPPORTED_LANGS.includes(short) ? short : 'en';
}

let initialized = false;

/** Idioma do documento, para leitores de tela e hifenização: pt vira pt-BR, a variante do produto. */
function markDocumentLang(lng: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = lng === 'pt' ? 'pt-BR' : lng;
}

/** Inicializa o i18n com o idioma ativo carregado dinamicamente. Chamar antes de renderizar. */
export async function setupI18n(lang: string): Promise<typeof i18n> {
  const lng = normalizeLang(lang);
  const dict = (await loaders[lng]()).default;

  if (!initialized) {
    i18n.on('languageChanged', markDocumentLang);
    await i18n.use(initReactI18next).init({
      resources: { [lng]: { translation: dict } },
      lng,
      fallbackLng: 'en',
      interpolation: { escapeValue: false }, // React já escapa
      returnNull: false,
      react: { useSuspense: false },
    });
    initialized = true;
  } else {
    if (!i18n.hasResourceBundle(lng, 'translation')) {
      i18n.addResourceBundle(lng, 'translation', dict);
    }
    await i18n.changeLanguage(lng);
  }
  return i18n;
}

/** Troca de idioma em runtime (carrega o bundle se necessário). */
export async function changeLanguage(lang: string): Promise<void> {
  const lng = normalizeLang(lang);
  if (!i18n.hasResourceBundle(lng, 'translation')) {
    const dict = (await loaders[lng]()).default;
    i18n.addResourceBundle(lng, 'translation', dict);
  }
  await i18n.changeLanguage(lng);
}

export default i18n;
